import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { useShift } from '../contexts/ShiftContext';
import { ChevronLeft, Clipboard, Check, AlertCircle, Plus, Trash2, DollarSign, Minus, CreditCard, HandCoins, ArrowUpFromLine, ArrowDownToLine, Lock, Truck, Banknote, Ban, Clock } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';
import { fmtMoney } from '../utils/money';
import { describirProductos } from '../components/SolicitudesCreditoPunto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function nombreProducto(clave) {
  return PRODUCTOS.find(p => String(p.clave) === String(clave))?.nombre || clave;
}

function describirEntrada(e) {
  if (e.tipo === 'dinero') return fmtMoney(e.monto);
  return Object.entries(e.productos || {})
    .filter(([, v]) => v)
    .map(([k, v]) => `${nombreProducto(k)} ${v}`)
    .join(', ');
}

function horaLocal(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' });
}

// Entradas (producto o dinero) que aún no se aceptan ni rechazan: bloquean el corte.
function EntradasPendientes({ pendientes, onIr }) {
  if (!pendientes?.length) return null;
  return (
    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl space-y-3" data-testid="entradas-pendientes">
      <div className="flex items-start gap-2">
        <Lock className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-red-800">No puedes cerrar el turno todavía</p>
          <p className="text-sm text-red-700">
            Tienes {pendientes.length} entrada{pendientes.length > 1 ? 's' : ''} pendiente{pendientes.length > 1 ? 's' : ''}.
            Acepta lo que sí recibiste y rechaza lo que no, antes de hacer el corte.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {pendientes.map(e => (
          <li key={e.id} className="flex items-center gap-2 bg-white border border-red-200 rounded-lg p-2.5">
            {e.tipo === 'dinero'
              ? <Banknote className="w-4 h-4 text-green-600 shrink-0" />
              : <Truck className="w-4 h-4 text-amber-500 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900">{e.origen_nombre}</p>
              <p className="text-xs text-zinc-600 break-words">{describirEntrada(e)}</p>
              <p className="text-[11px] text-zinc-400">Enviado por {e.creado_por_nombre} · {e.fecha_origen} {horaLocal(e.created_at)}</p>
            </div>
            <button
              onClick={() => onIr(e)}
              className="shrink-0 px-3 h-9 bg-red-600 text-white rounded-lg text-xs font-semibold"
            >
              Aceptar o rechazar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Solicitudes de crédito/abono que administración aún no aprueba ni rechaza: también bloquean el corte.
function SolicitudesPendientes({ solicitudes }) {
  if (!solicitudes?.length) return null;
  return (
    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl space-y-3" data-testid="solicitudes-pendientes">
      <div className="flex items-start gap-2">
        <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-amber-800">Esperando autorización de administración</p>
          <p className="text-sm text-amber-700">
            No puedes cerrar hasta que administración apruebe o rechace {solicitudes.length > 1 ? 'estas solicitudes' : 'esta solicitud'}.
            Avísales para que la revisen.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {solicitudes.map(s => (
          <li key={s.id} className="bg-white border border-amber-200 rounded-lg p-2.5">
            <p className="text-sm font-semibold text-zinc-900">{s.tipo_label} · {s.cliente_nombre}</p>
            <p className="text-xs text-zinc-600">
              {fmtMoney(s.tipo === 'venta_credito' ? s.total : s.monto)}
              {s.tipo === 'venta_credito' && ` · ${describirProductos(s.productos)}`}
            </p>
            <p className="text-[11px] text-zinc-400">Solicitada a las {horaLocal(s.created_at)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SolicitudesRechazadas({ rechazadas }) {
  if (!rechazadas?.length) return null;
  return (
    <div className="w-full max-w-sm mt-4 p-4 bg-red-50 border border-red-200 rounded-xl space-y-2 text-left" data-testid="solicitudes-rechazadas">
      <p className="text-xs font-bold text-red-800 uppercase tracking-wide flex items-center gap-1">
        <Ban className="w-4 h-4" /> Créditos y abonos rechazados por administración
      </p>
      {rechazadas.map(s => (
        <div key={s.id} className="text-sm border-t border-red-200 pt-2 first:border-0 first:pt-0">
          <p className="font-semibold text-zinc-800">
            {s.tipo_label} · {s.cliente_nombre}: {fmtMoney(s.tipo === 'venta_credito' ? s.total : s.monto)}
          </p>
          {s.tipo === 'venta_credito' && <p className="text-xs text-zinc-600">{describirProductos(s.productos)}</p>}
          <p className="text-xs text-red-700">Motivo: {s.motivo || '—'}</p>
          <p className="text-[11px] text-zinc-500">
            {s.tipo === 'venta_credito'
              ? 'El pollo entregado queda como faltante a tu cargo.'
              : 'El dinero recibido queda como sobrante y debes entregarlo.'}
          </p>
        </div>
      ))}
    </div>
  );
}

function EntradasRechazadas({ rechazadas }) {
  if (!rechazadas?.length) return null;
  return (
    <div className="w-full max-w-sm mt-4 p-4 bg-zinc-50 border border-zinc-300 rounded-xl space-y-2 text-left" data-testid="entradas-rechazadas">
      <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide flex items-center gap-1">
        <Ban className="w-4 h-4 text-red-500" /> Entradas rechazadas en este turno
      </p>
      {rechazadas.map(e => (
        <div key={e.id} className="text-sm border-t border-zinc-200 pt-2 first:border-0 first:pt-0">
          <p className="font-semibold text-zinc-800">{e.origen_nombre}: <span className="font-normal">{describirEntrada(e)}</span></p>
          <p className="text-xs text-zinc-500">Motivo: {e.motivo || '—'}</p>
          <p className="text-[11px] text-zinc-400">Rechazó {e.rechazado_por_nombre} a las {horaLocal(e.rechazado_at)}</p>
        </div>
      ))}
    </div>
  );
}

function ExcedenteFrio({ excedente }) {
  if (!excedente || Object.keys(excedente).length === 0) return null;
  return (
    <div className="mt-3 border border-amber-200 rounded-xl bg-amber-50 p-3 space-y-1">
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Pollo frío declarado de más</p>
      <p className="text-xs text-amber-700">
        Se declaró más pollo frío del que tiene el sistema. El inventario no se aumentó; queda marcado para revisión.
      </p>
      {Object.entries(excedente).map(([clave, cant]) => (
        <div key={clave} className="flex justify-between text-sm">
          <span className="text-zinc-700">{nombreProducto(clave)}</span>
          <span className="font-semibold text-amber-700">+{cant % 1 !== 0 ? cant.toFixed(1) : cant} pz</span>
        </div>
      ))}
    </div>
  );
}

function FaltanteProducto({ faltante, cargo, precios, sinPrecio }) {
  if (!faltante || Object.keys(faltante).length === 0) return null;
  return (
    <div className="mt-3 border border-red-200 rounded-xl bg-red-50 p-3 space-y-2">
      <p className="text-xs font-bold text-red-700 uppercase tracking-wide">Diferencia de producto</p>
      <div className="w-full text-xs text-zinc-500 grid grid-cols-3 gap-1 pb-1 border-b border-red-200">
        <span>Producto</span>
        <span className="text-center">Faltante</span>
        <span className="text-right">Cargo</span>
      </div>
      {Object.entries(faltante).map(([clave, cant]) => {
        const precio = precios ? (precios[clave] || precios[String(clave)] || 0) : 0;
        const subtotal = cant * precio;
        return (
          <div key={clave} className="grid grid-cols-3 gap-1 text-sm items-baseline">
            <span className="text-zinc-700">{nombreProducto(clave)}</span>
            <span className="text-center text-red-600 font-semibold">
              −{cant % 1 !== 0 ? cant.toFixed(1) : cant} pz
              {precio > 0 && <span className="text-zinc-400 font-normal text-xs"> × {fmtMoney(precio)}</span>}
            </span>
            <span className="text-right font-bold text-red-700">
              {subtotal > 0 ? `−${fmtMoney(subtotal)}` : '—'}
            </span>
          </div>
        );
      })}
      {cargo > 0 && (
        <div className="flex justify-between border-t border-red-200 pt-2">
          <span className="font-bold text-red-800 text-sm">Total cargo a nómina:</span>
          <span className="font-bold text-red-800 text-sm">−{fmtMoney(cargo)}</span>
        </div>
      )}
      {sinPrecio?.length > 0 && (
        <p className="text-xs text-red-700 border-t border-red-200 pt-2">
          Sin precio asignado (no se cobró): {sinPrecio.map(nombreProducto).join(', ')}
        </p>
      )}
    </div>
  );
}

export function Corte() {
  const { user } = useAuth();
  const { isOnline, addOfflineItem } = useOffline();
  const { refresh: refreshShift } = useShift();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmCorte, setShowConfirmCorte] = useState(false);
  const [corteExistente, setCorteExistente] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [solicitudesPend, setSolicitudesPend] = useState([]);
  const [showBloqueo, setShowBloqueo] = useState(false);
  
  // Form state
  const [efectivo, setEfectivo] = useState('');
  const [gastos, setGastos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [nuevoGastoCategoria, setNuevoGastoCategoria] = useState('');
  const [nuevoGastoNota, setNuevoGastoNota] = useState('');
  const [nuevoGastoMonto, setNuevoGastoMonto] = useState('');
  const [sobrantes, setSobrantes] = useState({});
  
  // Resumen data
  const [resumen, setResumen] = useState(null);
  const [precios, setPrecios] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  // Entradas sin aceptar ni rechazar: el backend no deja cerrar el turno mientras existan
  // y solicitudes de crédito/abono sin autorizar por administración
  const cargarPendientes = async () => {
    try {
      const { data } = await axios.get(`${API}/corte/bloqueos/${user.punto_venta_id}`);
      setPendientes(data.entradas || []);
      setSolicitudesPend(data.solicitudes || []);
      return (data.entradas || []).length + (data.solicitudes || []).length;
    } catch {
      return pendientes.length + solicitudesPend.length;
    }
  };

  const irAEntrada = (e) => {
    navigate(e.tipo === 'dinero' ? '/movimiento-dinero' : '/movimiento',
      { state: { tipo: 'entrada', volverA: '/corte' } });
  };

  const intentarCerrar = async () => {
    const actuales = isOnline ? await cargarPendientes() : pendientes.length + solicitudesPend.length;
    if (actuales > 0) {
      setShowBloqueo(true);
      return;
    }
    setShowConfirmCorte(true);
  };

  const loadData = async () => {
    if (!user?.punto_venta_id) {
      setError('No tienes un punto de venta asignado');
      setLoading(false);
      return;
    }
    
    try {
      // Check if corte already exists
      const { data: corte } = await axios.get(`${API}/corte/hoy/${user.punto_venta_id}`);
      if (corte) {
        setCorteExistente(corte);
      } else {
        await cargarPendientes();
      }

      // Get summary, prices, expense categories and persisted gastos in parallel
      const [resumenRes, preciosRes, catRes, gastosRes] = await Promise.allSettled([
        axios.get(`${API}/resumen/${user.punto_venta_id}`),
        axios.get(`${API}/precios/${user.punto_venta_id}`),
        axios.get(`${API}/categorias-gastos`),
        axios.get(`${API}/gastos/${user.punto_venta_id}`),
      ]);
      if (resumenRes.status === 'fulfilled') setResumen(resumenRes.value.data);
      if (preciosRes.status === 'fulfilled') setPrecios(preciosRes.value.data.precios || {});
      if (catRes.status === 'fulfilled') setCategorias(catRes.value.data);
      if (gastosRes.status === 'fulfilled') setGastos(gastosRes.value.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalVentas = () => resumen?.total_ventas_efectivo || 0;
  const calculateTotalCredito = () => resumen?.total_ventas_credito || 0;
  const calculateTotalAbonos = () => resumen?.total_abonos || 0;
  const calculateTotalEntradasDinero = () => resumen?.total_entradas_dinero || 0;
  const calculateTotalSalidasDinero = () => resumen?.total_salidas_dinero || 0;

  const addGasto = async () => {
    if (!nuevoGastoCategoria || !nuevoGastoMonto) return;
    const cat = categorias.find(c => c.id === nuevoGastoCategoria);
    const concepto = cat ? (nuevoGastoNota ? `${cat.nombre} – ${nuevoGastoNota}` : cat.nombre) : nuevoGastoNota || '—';
    const gasto = {
      punto_venta_id: user.punto_venta_id,
      concepto,
      monto: parseFloat(nuevoGastoMonto),
      categoria_id: nuevoGastoCategoria,
      categoria_nombre: cat?.nombre || '',
      nota: nuevoGastoNota,
    };
    try {
      const { data: res } = await axios.post(`${API}/gastos`, gasto);
      setGastos(prev => [...prev, { ...gasto, id: res.id }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar gasto');
      return;
    }
    setNuevoGastoCategoria('');
    setNuevoGastoNota('');
    setNuevoGastoMonto('');
  };

  const removeGasto = async (index) => {
    const gasto = gastos[index];
    if (gasto?.id) {
      try {
        await axios.delete(`${API}/gastos/${gasto.id}`);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al eliminar gasto');
        return;
      }
    }
    setGastos(prev => prev.filter((_, i) => i !== index));
  };

  const DECIMALES = ['1', '2']; // pechuga y pierna admiten medias piezas

  const handleSobranteIncrement = (clave) => {
    const step = DECIMALES.includes(clave) ? 0.5 : 1;
    setSobrantes(prev => ({ ...prev, [clave]: Math.round(((prev[clave] || 0) + step) * 10) / 10 }));
  };

  const handleSobranteDecrement = (clave) => {
    const step = DECIMALES.includes(clave) ? 0.5 : 1;
    setSobrantes(prev => ({ ...prev, [clave]: Math.max(0, Math.round(((prev[clave] || 0) - step) * 10) / 10) }));
  };

  const handleSobranteInput = (clave, value) => {
    if (value === '' || value === '-') { setSobrantes(prev => ({ ...prev, [clave]: 0 })); return; }
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setSobrantes(prev => ({ ...prev, [clave]: DECIMALES.includes(clave) ? num : Math.round(num) }));
  };

  const handleSobranteBlur = (clave, value) => {
    const num = parseFloat(value) || 0;
    const clean = DECIMALES.includes(clave)
      ? Math.max(0, Math.round(num * 2) / 2)
      : Math.max(0, Math.round(num));
    setSobrantes(prev => ({ ...prev, [clave]: clean }));
  };

  const totalGastos = gastos.reduce((acc, g) => acc + g.monto, 0);
  const totalVentas = calculateTotalVentas();
  const totalCredito = calculateTotalCredito();
  const totalAbonos = calculateTotalAbonos();
  const totalEntradasDinero = calculateTotalEntradasDinero();
  const totalSalidasDinero = calculateTotalSalidasDinero();
  const efectivoNum = parseFloat(efectivo) || 0;
  const esperado = totalVentas + totalAbonos + totalEntradasDinero - totalGastos - totalSalidasDinero;
  const diferencia = efectivoNum - esperado;

  const handleSubmit = async () => {
    if (!efectivo) {
      setError('Ingresa el efectivo en caja');
      return;
    }
    
    setError('');
    setSubmitting(true);
    
    const data = {
      punto_venta_id: user.punto_venta_id,
      efectivo: efectivoNum,
      gastos,
      sobrantes,
      fecha: new Date().toISOString().split('T')[0],
    };
    
    if (isOnline) {
      try {
        const { data: resp } = await axios.post(`${API}/corte`, data);
        setSubmitResponse(resp);
        await refreshShift();
        setSuccess(true);
      } catch (error) {
        setError(error.response?.data?.detail || 'Error al registrar corte');
        const actuales = await cargarPendientes();
        if (actuales > 0) setShowBloqueo(true);
      }
    } else {
      addOfflineItem('cortes', data);
      setSuccess(true);
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2">Corte registrado</h1>
        <p className="text-zinc-500 mb-4">El día ha sido cerrado exitosamente</p>
        <div className="w-full max-w-sm">
          <FaltanteProducto
            faltante={submitResponse?.faltante_productos}
            cargo={submitResponse?.cargo_nomina_productos}
            precios={precios}
            sinPrecio={submitResponse?.faltante_sin_precio}
          />
          <ExcedenteFrio excedente={submitResponse?.excedente_frio} />
          <EntradasRechazadas rechazadas={submitResponse?.entradas_rechazadas} />
          <SolicitudesRechazadas rechazadas={submitResponse?.solicitudes_rechazadas} />
        </div>
        <button
          onClick={() => navigate('/pos')}
          className="mt-6 px-8 py-3 bg-zinc-900 text-white rounded-xl font-medium"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  // If corte already exists
  if (corteExistente) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="corte-page">
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pos')}
              className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-zinc-900">Corte de Caja</h1>
          </div>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Corte ya registrado</h2>
          <p className="text-zinc-500 mb-6">Ya completaste el corte de caja de hoy</p>
          
          <div className="w-full max-w-sm bg-zinc-50 rounded-xl p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-zinc-600">Ventas efectivo:</span>
              <span className="font-bold text-green-600">+{fmtMoney(corteExistente.total_ventas_calculado)}</span>
            </div>
            {corteExistente.total_abonos > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <HandCoins className="w-4 h-4 text-green-500" />
                  Abonos cobrados:
                </span>
                <span className="font-bold text-green-600">+{fmtMoney(corteExistente.total_abonos)}</span>
              </div>
            )}
            {(corteExistente.total_entradas_dinero || 0) > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <ArrowDownToLine className="w-4 h-4 text-emerald-500" />
                  Entradas de dinero:
                </span>
                <span className="font-bold text-emerald-600">+{fmtMoney(corteExistente.total_entradas_dinero)}</span>
              </div>
            )}
            {corteExistente.total_ventas_credito > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  Ventas a crédito:
                </span>
                <span className="font-bold text-blue-600">{fmtMoney(corteExistente.total_ventas_credito)}</span>
              </div>
            )}
            {corteExistente.total_gastos > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-600">Gastos:</span>
                <span className="font-bold text-red-600">-{fmtMoney(corteExistente.total_gastos)}</span>
              </div>
            )}
            {(corteExistente.total_salidas_dinero || 0) > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <ArrowUpFromLine className="w-4 h-4 text-orange-500" />
                  Salidas de dinero:
                </span>
                <span className="font-bold text-orange-600">-{fmtMoney(corteExistente.total_salidas_dinero)}</span>
              </div>
            )}
            <div className="border-t border-zinc-200 pt-2 flex justify-between text-sm text-zinc-500">
              <span>Efectivo declarado:</span>
              <span className="font-semibold text-zinc-800">{fmtMoney(corteExistente.efectivo)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-bold">Diferencia:</span>
              <span className={`font-bold ${corteExistente.diferencia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {corteExistente.diferencia >= 0 ? '+' : ''}{fmtMoney(corteExistente.diferencia)}
              </span>
            </div>
          </div>
          {(user?.role === 'admin' || user?.role === 'propietario') && (
            <button
              onClick={() => navigate(
                `/admin/bitacora?punto=${user.punto_venta_id}` +
                `&fecha=${corteExistente.fecha || new Date().toISOString().split('T')[0]}`
              )}
              className="mt-3 w-full h-11 border border-zinc-300 text-zinc-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2 active:bg-zinc-50"
              data-testid="btn-ver-bitacora-turno"
            >
              <Clipboard className="w-4 h-4" />
              Ver bitácora del turno
            </button>
          )}
          <FaltanteProducto
            faltante={corteExistente.faltante_productos}
            cargo={corteExistente.cargo_nomina_productos}
            precios={precios}
            sinPrecio={corteExistente.faltante_sin_precio}
          />
          <ExcedenteFrio excedente={corteExistente.excedente_frio} />
          <EntradasRechazadas rechazadas={corteExistente.entradas_rechazadas} />
          <SolicitudesRechazadas rechazadas={corteExistente.solicitudes_rechazadas} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="corte-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pos')}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Corte de Caja</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        <EntradasPendientes pendientes={pendientes} onIr={irAEntrada} />
        <SolicitudesPendientes solicitudes={solicitudesPend} />

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Summary */}
        <div className="bg-zinc-50 rounded-xl p-4" data-testid="corte-summary">
          <h3 className="text-sm font-medium text-zinc-500 mb-3 flex items-center gap-2">
            <Clipboard className="w-4 h-4" />
            RESUMEN DEL DÍA
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-zinc-600">Ventas efectivo:</span>
              <span className="font-bold text-green-600">+{fmtMoney(totalVentas)}</span>
            </div>
            {totalAbonos > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <HandCoins className="w-4 h-4 text-green-500" />
                  Abonos cobrados:
                </span>
                <span className="font-bold text-green-600">+{fmtMoney(totalAbonos)}</span>
              </div>
            )}
            {totalEntradasDinero > 0 && (
              <div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-600 flex items-center gap-1">
                    <ArrowDownToLine className="w-4 h-4 text-emerald-500" />
                    Entradas de dinero:
                  </span>
                  <span className="font-bold text-emerald-600">+{fmtMoney(totalEntradasDinero)}</span>
                </div>
                {resumen?.entradas_dinero?.map(e => (
                  <div key={e.id} className="flex justify-between text-xs text-zinc-400 pl-5 mt-0.5">
                    <span>De {e.origen_nombre}{e.nota ? ` · ${e.nota}` : ''}</span>
                    <span>{fmtMoney(e.monto_confirmado ?? e.monto)}</span>
                  </div>
                ))}
              </div>
            )}
            {totalCredito > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-zinc-600 flex items-center gap-1">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  Ventas a crédito:
                </span>
                <span className="font-bold text-blue-600">{fmtMoney(totalCredito)}</span>
              </div>
            )}
            {totalGastos > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-600">Gastos:</span>
                <span className="font-bold text-red-600">-{fmtMoney(totalGastos)}</span>
              </div>
            )}
            {totalSalidasDinero > 0 && (
              <div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-600 flex items-center gap-1">
                    <ArrowUpFromLine className="w-4 h-4 text-orange-500" />
                    Salidas de dinero:
                  </span>
                  <span className="font-bold text-orange-600">-{fmtMoney(totalSalidasDinero)}</span>
                </div>
                {resumen?.salidas_dinero?.map(s => (
                  <div key={s.id} className="flex justify-between text-xs text-zinc-400 pl-5 mt-0.5">
                    <span>
                      A {s.destino_nombre}{s.nota ? ` · ${s.nota}` : ''}
                      {s.status === 'en_transito' && <span className="ml-1 text-amber-500">(en tránsito)</span>}
                    </span>
                    <span>{fmtMoney(s.monto)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-zinc-200 pt-2 flex justify-between">
              <span className="font-bold text-zinc-900">Esperado en caja:</span>
              <span className="text-xl font-bold text-blue-600">{fmtMoney(esperado)}</span>
            </div>
            {totalCredito > 0 && (
              <p className="text-xs text-zinc-400">* Las ventas a crédito no se incluyen en el esperado de caja</p>
            )}
          </div>
        </div>

        {/* Efectivo Input */}
        <div>
          <label className="block text-zinc-500 text-sm font-medium tracking-wide mb-2">
            <DollarSign className="w-4 h-4 inline mr-1" />
            EFECTIVO EN CAJA
          </label>
          <input
            type="number"
            value={efectivo}
            onChange={(e) => setEfectivo(e.target.value)}
            placeholder="0.00"
            className="w-full h-16 px-4 bg-white border border-zinc-300 rounded-xl text-3xl font-bold text-center"
            data-testid="input-efectivo"
          />
          
          {efectivoNum > 0 && (
            <div className={`mt-3 p-3 rounded-xl ${diferencia >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className={diferencia >= 0 ? 'text-green-700' : 'text-red-700'}>
                  {diferencia >= 0 ? 'Sobrante:' : 'Faltante:'}
                </span>
                <span className={`font-bold text-xl ${diferencia >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {fmtMoney(Math.abs(diferencia))}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Gastos */}
        <div>
          <h3 className="text-zinc-500 text-sm font-medium tracking-wide mb-3">GASTOS DEL DÍA</h3>

          {/* List of gastos */}
          {gastos.length > 0 && (
            <div className="space-y-2 mb-4">
              {gastos.map((gasto, index) => (
                <div key={index} className="flex items-center justify-between bg-zinc-50 rounded-lg p-3">
                  <div>
                    <p className="font-medium text-sm">{gasto.categoria_nombre || gasto.concepto}</p>
                    {gasto.nota && <p className="text-xs text-zinc-500">{gasto.nota}</p>}
                    <p className="text-red-600 font-bold">{fmtMoney(gasto.monto)}</p>
                  </div>
                  <button
                    onClick={() => removeGasto(index)}
                    className="w-10 h-10 flex items-center justify-center bg-white rounded-lg text-red-500"
                    data-testid={`remove-gasto-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add gasto form */}
          <div className="space-y-2">
            {categorias.length > 0 ? (
              <select
                value={nuevoGastoCategoria}
                onChange={(e) => setNuevoGastoCategoria(e.target.value)}
                className="w-full h-12 px-4 border border-zinc-200 rounded-lg bg-white text-zinc-900"
                data-testid="select-gasto-categoria"
              >
                <option value="">Selecciona categoría...</option>
                {categorias.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Sin categorías configuradas. El administrador debe crearlas en Ajustes → Categorías de gastos.
              </p>
            )}
            <input
              type="text"
              value={nuevoGastoNota}
              onChange={(e) => setNuevoGastoNota(e.target.value)}
              placeholder="Nota / descripción (opcional)"
              className="w-full h-11 px-4 border border-zinc-200 rounded-lg text-sm"
              data-testid="input-gasto-nota"
            />
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={nuevoGastoMonto}
                onChange={(e) => setNuevoGastoMonto(e.target.value)}
                placeholder="$0.00"
                className="flex-1 h-12 px-4 border border-zinc-200 rounded-lg"
                data-testid="input-gasto-monto"
              />
              <button
                onClick={addGasto}
                disabled={!nuevoGastoCategoria || !nuevoGastoMonto}
                className="h-12 px-6 bg-zinc-900 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="btn-add-gasto"
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            </div>
          </div>
        </div>

        {/* Sobrantes */}
        <div>
          <h3 className="text-zinc-500 text-sm font-medium tracking-wide mb-3">SOBRANTE DE PRODUCTO</h3>

          <div className="space-y-1">
            {PRODUCTOS.map((producto) => {
              const qty = sobrantes[producto.clave] ?? 0;
              const esDecimal = DECIMALES.includes(producto.clave);
              return (
                <div
                  key={producto.clave}
                  className="flex items-center justify-between py-3 border-b border-zinc-100"
                >
                  <div>
                    <span className="text-zinc-900">{producto.nombre}</span>
                    {esDecimal && <span className="ml-2 text-xs text-zinc-400">(.5 o entera)</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSobranteDecrement(producto.clave)}
                      disabled={qty === 0}
                      className="w-10 h-10 flex items-center justify-center border border-zinc-200 rounded-lg disabled:opacity-30"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      inputMode={esDecimal ? 'decimal' : 'numeric'}
                      min="0"
                      step={esDecimal ? '0.5' : '1'}
                      value={qty}
                      onChange={(e) => handleSobranteInput(producto.clave, e.target.value)}
                      onBlur={(e) => handleSobranteBlur(producto.clave, e.target.value)}
                      className="w-16 h-10 text-center border border-zinc-200 rounded-lg font-bold text-base focus:outline-none focus:ring-2 focus:ring-zinc-400"
                    />
                    <button
                      onClick={() => handleSobranteIncrement(producto.clave)}
                      className="w-10 h-10 flex items-center justify-center border border-zinc-200 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 pb-8 pt-4 border-t border-zinc-200">
        <button
          onClick={intentarCerrar}
          disabled={submitting || !efectivo}
          className="w-full h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="btn-submit-corte"
        >
          {submitting ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              Cerrar Corte del Día
            </>
          )}
        </button>
      </div>

      {/* Bloqueo: entradas pendientes de aceptar o rechazar */}
      {showBloqueo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl max-h-[90vh] overflow-auto space-y-4" data-testid="dialogo-bloqueo-corte">
            <EntradasPendientes pendientes={pendientes} onIr={(e) => { setShowBloqueo(false); irAEntrada(e); }} />
            <SolicitudesPendientes solicitudes={solicitudesPend} />
            <button
              onClick={() => setShowBloqueo(false)}
              className="w-full h-12 bg-zinc-100 rounded-xl font-bold text-zinc-700"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Diálogo de confirmación */}
      {showConfirmCorte && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 mb-2">¿Cerrar corte de caja?</h2>
              <p className="text-zinc-500 text-sm leading-relaxed">
                Al registrar el corte <span className="font-semibold text-zinc-700">no podrás registrar más ventas ni movimientos</span> en este turno. Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowConfirmCorte(false)}
                className="h-12 bg-zinc-100 rounded-xl font-bold text-zinc-700"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setShowConfirmCorte(false); handleSubmit(); }}
                className="h-12 bg-zinc-900 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                data-testid="btn-confirm-corte"
              >
                <Check className="w-4 h-4" />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
